import { useViewport } from '@yoweb/ui/hooks/useViewport';
import { Spacer } from '@yoweb/ui/components/Spacer';
import type { SliceTemplateProps } from '@yoweb/prismic-slicemachine/slices/slicesTypes';
import SeoSlice, { SeoSliceType } from '@yoweb/prismic-slicemachine/slices/SeoSlice';
import DividerSlice, { DividerSliceType } from '@yoweb/prismic-slicemachine/slices/DividerSlice';
import HeroSectionSlice, {
  HeroSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/HeroSectionSlice';
import LargeMediaSectionSlice, {
  LargeMediaSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/LargeMediaSectionSlice';
import HeroWithFormSlice, {
  HeroWithFormSliceType,
} from '@yoweb/prismic-slicemachine/slices/HeroWithFormSlice';
import ContentImageSectionSlice, {
  ContentImageSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/ContentImageSectionSlice';
import ExplanationSectionSlice, {
  ExplanationSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/ExplanationSectionSlice';
import TestimonialSectionSlice, {
  TestimonialSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/TestimonialSectionSlice';
import ColoredCtaSectionSlice, {
  ColoredCtaSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/ColoredCtaSectionSlice';
import AppDownloadCtaSectionSlice, {
  AppDownloadCtaSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/AppDownloadCtaSectionSlice';
import ColoredCtaWithFormSlice, {
  ColoredCtaWithFormSliceType,
} from '@yoweb/prismic-slicemachine/slices/ColoredCtaWithFormSlice';
import SmallPrintSlice, {
  SmallPrintSliceType,
} from '@yoweb/prismic-slicemachine/slices/SmallPrintSlice';
import CarouselStatsSlice, {
  CarouselStatsSliceType,
} from '@yoweb/prismic-slicemachine/slices/CarouselStatsSlice';
import ColoredCtaWithFigureSlice, {
  ColoredCtaWithFigureSliceType,
} from '@yoweb/prismic-slicemachine/slices/ColoredCtaWithFigureSlice';
import CornerCardSectionSlice, {
  CornerCardSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/CornerCardSectionSlice';
import DreamGallerySlice, {
  DreamGallerySliceType,
} from '@yoweb/prismic-slicemachine/slices/DreamGallerySlice';
import FaqSlice, { FaqSliceType } from '@yoweb/prismic-slicemachine/slices/FaqSlice';
import HeroWithColoredBackgroundSlice, {
  HeroWithColoredBackgroundSliceType,
} from '@yoweb/prismic-slicemachine/slices/HeroWithColoredBackgroundSlice';
import ImageHeaderSlice, {
  ImageHeaderSliceType,
} from '@yoweb/prismic-slicemachine/slices/ImageHeaderSlice';
import ImagesWithQuoteSlice, {
  ImagesWithQuoteSliceType,
} from '@yoweb/prismic-slicemachine/slices/ImagesWithQuoteSlice';
import MarketingCarouselSlice, {
  MarketingCarouselSliceType,
} from '@yoweb/prismic-slicemachine/slices/MarketingCarouselSlice';
import MediaHeaderSlice, {
  MediaHeaderSliceType,
} from '@yoweb/prismic-slicemachine/slices/MediaHeaderSlice';
import ParagraphSlice, {
  ParagraphSliceType,
} from '@yoweb/prismic-slicemachine/slices/ParagraphSlice';
import ProjectCardSlice, {
  ProjectCardSliceType,
} from '@yoweb/prismic-slicemachine/slices/ProjectCardSlice';
import RoundedImageSlice, {
  RoundedImageSliceType,
} from '@yoweb/prismic-slicemachine/slices/RoundedImageSlice';
import VerticalScrollCarouselSlice, {
  VerticalScrollCarouselSliceType,
} from '@yoweb/prismic-slicemachine/slices/VerticalScrollCarouselSlice';
import VideoColoredSectionSlice, {
  VideoColoredSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/VideoColoredSectionSlice';
import CardCarouselSectionSlice, {
  CardCarouselSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/CardCarouselSectionSlice';
import ChatMessageSlice, {
  ChatMessageSliceType,
} from '@yoweb/prismic-slicemachine/slices/ChatMessageSlice';
import type { ChatMessageSliceResponse } from '@yoweb/prismic-slicemachine/slices/ChatMessageSlice/ChatMessageSliceTypes';
import { ErrorDisplay } from '@yoweb/ui/components/form';
import AccordionColorGroupsSectionSlice, {
  AccordionColorGroupsSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/AccordionColorGroupsSectionSlice';
import AnimatedColoredSectionSlice, {
  AnimatedColoredSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/AnimatedColoredSectionSlice';
import JugglingALot from '@yoweb/ui/lottieAnimations/juggling-a-lot.json';

export const TargetedLandingPageTemplate = ({
  slice,
  slices,
  children,
}: SliceTemplateProps): JSX.Element | null => {
  const { viewportWidth } = useViewport();

  switch (slice.slice_type) {
    case AccordionColorGroupsSectionSliceType:
      return (
        <AccordionColorGroupsSectionSlice
          data-testid="accordion-color-groups-slice"
          slice={slice}
        />
      );

    case AnimatedColoredSectionSliceType:
      return <AnimatedColoredSectionSlice slice={slice} animationData={JugglingALot} />;

    case VideoColoredSectionSliceType:
      return <VideoColoredSectionSlice slice={slice} />;

    case VerticalScrollCarouselSliceType:
      return <VerticalScrollCarouselSlice slice={slice} />;

    case RoundedImageSliceType:
      return <RoundedImageSlice slice={slice} />;

    case ParagraphSliceType:
      return <ParagraphSlice slice={slice} />;

    case ProjectCardSliceType:
      return <ProjectCardSlice slice={slice} />;

    case MediaHeaderSliceType:
      return <MediaHeaderSlice slice={slice} />;

    case MarketingCarouselSliceType:
      return <MarketingCarouselSlice slice={slice} />;

    case ImagesWithQuoteSliceType:
      return <ImagesWithQuoteSlice slice={slice} />;

    case ImageHeaderSliceType:
      return <ImageHeaderSlice slice={slice} />;

    case HeroWithColoredBackgroundSliceType:
      return <HeroWithColoredBackgroundSlice slice={slice} />;

    case FaqSliceType:
      return <FaqSlice slice={slice} />;

    case DreamGallerySliceType:
      return <DreamGallerySlice slice={slice} />;

    case CornerCardSectionSliceType:
      return <CornerCardSectionSlice slice={slice} />;

    case ColoredCtaWithFigureSliceType:
      return <ColoredCtaWithFigureSlice slice={slice} />;

    case CarouselStatsSliceType:
      return <CarouselStatsSlice slice={slice} />;

    case SeoSliceType:
      return <SeoSlice slice={slice} />;

    case LargeMediaSectionSliceType:
      return <LargeMediaSectionSlice slice={slice} />;

    case HeroWithFormSliceType:
      return <HeroWithFormSlice slice={slice}>{children}</HeroWithFormSlice>;

    case HeroSectionSliceType:
      return (
        <HeroSectionSlice
          slice={slice}
          mobilePosition="top center"
          useMobileProps
          contentMaxWidth={{ md: 448 }}
          mobileImageHeight={viewportWidth < 768 ? 520 : 720}
          withCard={viewportWidth < 1024 ? false : true}
        />
      );

    case ContentImageSectionSliceType:
      return <ContentImageSectionSlice slice={slice} />;

    case ExplanationSectionSliceType:
      return <ExplanationSectionSlice slice={slice} pt={{ _: 'large3', md: 'large2', lg: 104 }} />;

    case TestimonialSectionSliceType:
      return <TestimonialSectionSlice slice={slice} />;

    case ColoredCtaSectionSliceType:
      return (
        <ColoredCtaSectionSlice
          slice={slice}
          atBottom
          pt={{ _: 'large3', md: 'large2', lg: 104 }}
          backgroundColor="sky"
        />
      );

    case AppDownloadCtaSectionSliceType:
      return (
        <AppDownloadCtaSectionSlice
          slice={slice}
          pt={{ _: 'large3', md: 'large2', lg: 104 }}
          backgroundColor="sky"
        />
      );

    case ColoredCtaWithFormSliceType:
      return <ColoredCtaWithFormSlice slice={slice}>{children}</ColoredCtaWithFormSlice>;

    case DividerSliceType:
      return <DividerSlice slice={slice} />;

    case SmallPrintSliceType:
      return (
        <>
          <Spacer size={{ _: 'medium2', lg: 'small3' }} />
          <SmallPrintSlice slice={slice} />
          <Spacer size="normal3" />
        </>
      );

    case CardCarouselSectionSliceType: {
      const chatMessageSlice = slices.find(
        (_slice) => _slice.slice_type === ChatMessageSliceType,
      ) as ChatMessageSliceResponse | undefined;

      return (
        <CardCarouselSectionSlice
          slice={slice}
          id={`chat-reveal-carousel-section-${slice.primary.title
            .toLowerCase()
            .split(' ')
            .join('-')}`}
          background="base050"
        >
          {chatMessageSlice
            ? slice.items.map((item) => (
                <ChatMessageSlice
                  key={item.label}
                  slice={chatMessageSlice}
                  cardCarouselItem={item}
                />
              ))
            : // eslint-disable-next-line react/jsx-no-literals
              [
                <ErrorDisplay key="missing" title={`Prismic CMS error occurred.`} withBorder>
                  {
                    // eslint-disable-next-line react/jsx-no-literals
                    'CardCarouselSectionSlice requires ChatMessageSlice. Please add data to the ChatMessageSlice.'
                  }
                </ErrorDisplay>,
              ]}
        </CardCarouselSectionSlice>
      );
    }

    default:
      return null;
  }
};
